import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Check } from "lucide-react";
import { Layout } from "../components/Layout";
import { LotusMark } from "../components/Logo";
import { RegionSwitcher } from "../components/RegionSwitcher";
import { useRegion } from "../hooks/useRegion";
import { pricing, regions } from "../data/pricing";
import { siteConfig } from "../data/siteConfig";

export const PricingPage = () => {
  const [region, setRegion] = useRegion();
  const data = pricing[region];
  const currency = data.currency;

  return (
    <Layout>
      <section className="pt-28 md:pt-32 pb-12">
        <div className="container mx-auto px-6 text-center">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-widest mb-5">
            <LotusMark className="w-4 h-4" /> Pricing
          </span>
          <h1 className="font-heading text-4xl md:text-5xl font-semibold text-charcoal mb-4">
            Simple, transparent plans
          </h1>
          <p className="text-charcoal/70 max-w-2xl mx-auto text-lg mb-8">
            Choose what fits your lifestyle. Prices shown for{" "}
            <span className="font-semibold text-primary">
              {regions[region].label}
            </span>{" "}
            — switch your region below.
          </p>
          <RegionSwitcher region={region} setRegion={setRegion} />
        </div>
      </section>

      <section className="pb-16">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-7 items-stretch">
            {data.plans.map((plan) => (
              <div
                key={plan.key}
                className={`pricing-card ${plan.featured ? "featured" : ""}`}
              >
                {plan.featured && (
                  <div className="featured-badge">{plan.tagline}</div>
                )}
                <h3 className="font-heading text-2xl text-charcoal mb-1">
                  {plan.name}
                </h3>
                {!plan.featured && (
                  <p className="text-charcoal/50 text-sm mb-6">{plan.tagline}</p>
                )}
                {plan.featured && <div className="mb-6" />}
                <div className="mb-7">
                  <span className="font-heading text-4xl font-semibold text-charcoal">
                    {currency} {plan.price}
                  </span>
                  <span className="text-charcoal/50 text-sm ml-1">
                    {plan.unit}
                  </span>
                </div>
                <ul className="space-y-3 mb-8 flex-1">
                  {plan.features.map((f) => (
                    <li
                      key={f}
                      className="flex items-start gap-2.5 text-sm text-charcoal/75"
                    >
                      <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
                <a
                  href={siteConfig.whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`block text-center py-3 rounded-full font-semibold transition-all ${
                    plan.featured
                      ? "bg-primary text-white shadow-soft hover:bg-primary-dark"
                      : "border border-accent/40 text-primary hover:bg-cream-dark"
                  }`}
                >
                  Get started
                </a>
              </div>
            ))}
          </div>

          <p className="text-center text-xs text-charcoal/50 mt-8 max-w-2xl mx-auto">
            All prices are indicative and may vary by program. Corporate and
            workshop sessions are quoted individually — please get in touch for a
            tailored plan.
          </p>
        </div>
      </section>

      {/* Private CTA */}
      <section className="pb-24">
        <div className="container mx-auto px-6">
          <div className="bg-cream-dark rounded-[2.5rem] p-10 md:p-16 flex flex-col md:flex-row items-center gap-10">
            <div className="md:w-2/3">
              <h2 className="font-heading text-3xl text-charcoal mb-4">
                Need something more personal?
              </h2>
              <p className="text-charcoal/70 text-lg">
                1-on-1 and small-group sessions are tailored to your goals,
                health needs and schedule — available online and in Hong Kong.
                Reach out and we'll design the right plan together.
              </p>
            </div>
            <div className="md:w-1/3 w-full">
              <RouterLink
                to="/contact"
                className="block text-center py-4 bg-primary text-white rounded-full font-semibold shadow-soft hover:bg-primary-dark transition-all"
              >
                Enquire now
              </RouterLink>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default PricingPage;
